import type { Web3WithdrawAmountProps } from "@/components/Web3/Withdraw/Amount/Wrapper";
import React, { useCallback, useEffect, useState } from "react";

import { FixedNumber } from "@ethersproject/bignumber";
import { type NumberFormatValues, NumericFormat } from "react-number-format";
import { classNames, getVaultDecimals } from "@/utils";

export default function Web3WithdrawAmountInput({ singleVault, setVaultList }: Web3WithdrawAmountProps) {
  const tokenPrice = singleVault.balanceInFiat?.divUnsafe(singleVault.balance || FixedNumber.from(1)) || FixedNumber.from(1);

  const [inputValue, setInputValue] = useState<FixedNumber | undefined>();

  const [usdValue, setUsdValue] = useState<FixedNumber | undefined>();

  useEffect(() => {
    setInputValue(singleVault.amount);
    setUsdValue(singleVault.amountInFiat);
  }, [singleVault]);

  const validateAmount = (values: NumberFormatValues) => {
    const { value, floatValue } = values;
    if (!value || !floatValue) {
      setUsdValue(undefined);
      return true;
    }
    try {
      const inAmountTyped = FixedNumber.from(`${value}`);
      const isValid = !inAmountTyped.isZero() && !inAmountTyped.isNegative();
      if (isValid) {
        setUsdValue(inAmountTyped);
      }
      return isValid;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    setUsdValue(singleVault.amountInFiat);
  }, [singleVault.amountInFiat]);

  const handleAmountChange = useCallback(
    (values: NumberFormatValues) => {
      const { value, floatValue } = values;
      if (value && floatValue && !FixedNumber.from(value).isZero()) {
        setVaultList(currentVaultList =>
          currentVaultList.map(vault => {
            if (vault.id === singleVault.id) {
              return {
                ...vault,
                amount: FixedNumber.from(value),
                amountInFiat: FixedNumber.from(value).mulUnsafe(tokenPrice),
              };
            } else {
              return vault;
            }
          })
        );
      }
    },
    [setVaultList, singleVault.id, tokenPrice]
  );

  return (
    <div className="flex w-full flex-col space-y-[26px]">
      <div className="flex w-full flex-col space-y-[4px] border-[.5px] border-primary/16 bg-primary-100/10 p-[8px]">
        <div className="flex justify-between space-x-[4px]">
          <NumericFormat
            autoFocus
            defaultValue={"0.0"}
            value={inputValue?.toString()}
            className="h-[20px] w-full bg-[transparent] text-left text-[24px] leading-[20px] placeholder-[#d4d4d4] focus:outline-0"
            isAllowed={validateAmount}
            onValueChange={handleAmountChange}
            decimalScale={getVaultDecimals(singleVault)}
            decimalSeparator={"."}
            id={`${singleVault.slug}-amount`}
          />
          <span>{singleVault.symbol}</span>
        </div>
        <div className="flex space-x-[4px]">
          <span className={classNames(!usdValue && "invisible")}>${usdValue?.round(2).toString()}</span>
        </div>
      </div>
    </div>
  );
}
